import React, {Component} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import {WHITE, ASH} from '../../../../shared/style/colors';
import {debounce} from '../../../../shared/utils/debounce';
import createServicesIndex from './createServicesIndex';
import ComparisonItem from './comparison-item';
import ComparisonInputLoadingIndicator from './comparison-input-loading-indicator';
import ComparisonInputResult from './comparison-input-result';

const Z_INDEX = 1000;

const Wrapper = glamorous(ComparisonItem)({
  position: 'relative'
});

const InnerSearchInput = glamorous.input({
  background: 'transparent',
  margin: 0,
  padding: '5px 10px',
  border: 'none',
  flexGrow: 1,
  outline: 'none',
  borderRadius: 3,
  width: 140
});

const InvisibleOverlay = glamorous.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: Z_INDEX - 1
});

const ResultList = glamorous.ul({
  backgroundColor: WHITE,
  border: `1px solid ${ASH}`,
  boxShadow: '0 19px 54px rgba(0, 0, 0, 0.2)',
  listStyleType: 'none',
  margin: 0,
  padding: 0,
  maxHeight: 300,
  overflowY: 'auto',
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  zIndex: Z_INDEX,
  userSelect: 'none'
});

export default class ComparisonInput extends Component {
  static defaultProps = {
    placeholder: ''
  };
  static propTypes = {
    placeholder: PropTypes.string.isRequired,
    onSearch: PropTypes.func,
    onSearchItemAdded: PropTypes.func.isRequired,
    filterFn: PropTypes.func,
    id: PropTypes.number
  };

  servicesIndex = createServicesIndex();
  inputRef = React.createRef();
  state = {
    searchResults: [],
    searchInput: '',
    querying: false,
    selectedIndex: -1
  };

  focusInput = () => {
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  componentDidMount() {
    if (this.inputRef && this.inputRef.current) {
      this.setRef();
    }
  }

  setRef = () => {
    if (this.inputRef.current.id === '0') {
      this.focusInput();
    }
  };

  clearSearchResults = () => {
    this.setState({
      searchResults: [],
      searchInput: '',
      querying: false,
      selectedIndex: -1
    });

    this.focusInput();
  };

  search = debounce(async query => {
    const {filterFn} = this.props;

    const {hits} = await this.servicesIndex.search(query, {
      facets: 'Service',
      filters: 'is_approved = 1',
      attributesToRetrieve: ['id', 'name', 'slug', 'image_url'],
      typoTolerance: 'false',
      hitsPerPage: 20
    });

    this.setState({
      searchResults: hits.filter(
        service => Boolean(service.image_url) && (!filterFn || filterFn(service))
      ),
      querying: false
    });
  }, 500);

  searchChange = event => {
    const {onSearch} = this.props;
    const query = event.target.value;

    if (!query || query === '') {
      this.clearSearchResults();
      return;
    }

    if (onSearch) {
      onSearch(query);
    }

    this.search(query);

    this.setState({
      searchInput: query,
      searchResults: [],
      querying: true
    });
  };

  searchKeyDown = e => {
    const {searchResults, selectedIndex} = this.state;
    const selectedService = searchResults[selectedIndex] || null;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (selectedIndex >= searchResults.length - 1) return;
        this.setState({selectedIndex: selectedIndex + 1});
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (selectedIndex <= -1) return;
        this.setState({selectedIndex: selectedIndex - 1});
        break;
      }
      case 'Enter': {
        if (selectedService) {
          this.addSelectedTool(selectedService);
        }
        break;
      }
      case 'Escape': {
        this.clearSearchResults();
        break;
      }
    }
  };

  addSelectedTool = tool => {
    const {onSearchItemAdded} = this.props;
    this.clearSearchResults();
    onSearchItemAdded(tool);
  };

  render() {
    const {placeholder, id} = this.props;
    const {searchInput, querying, selectedIndex, searchResults} = this.state;

    return (
      <Wrapper onClick={this.focusInput}>
        <InnerSearchInput
          data-testid="searchForTool"
          id={id}
          innerRef={this.inputRef}
          placeholder={placeholder}
          onChange={this.searchChange}
          onKeyDown={this.searchKeyDown}
          value={searchInput}
        />
        {(searchResults.length > 0 || querying) && (
          <div>
            <InvisibleOverlay onClick={this.clearSearchResults} />
            <ResultList data-testid="stackupToolResultList">
              {querying && <ComparisonInputLoadingIndicator />}
              {searchResults.map((service, index) => (
                <ComparisonInputResult
                  key={service.id}
                  service={service}
                  selected={selectedIndex === index}
                  onClick={() => this.addSelectedTool(service)}
                  onMouseEnter={() => this.setState({selectedIndex: index})}
                />
              ))}
            </ResultList>
          </div>
        )}
      </Wrapper>
    );
  }
}
